export type Platform = 'ios' | 'android' | 'unknown';

const STORE_URL_IOS = 'https://apps.apple.com/in/app/slayd-app/id6749233916';
const STORE_URL_ANDROID =
  'https://play.google.com/store/apps/details?id=com.slayd.tech';
const IN_APP_BROWSER_REGEX = /FBAN|FBAV|Instagram|Twitter|Line/i;
const SAFARI_REGEX = /^((?!chrome|android|crios|fxios).)*safari/i;

export const getPlatform = (): Platform => {
  const userAgent = navigator.userAgent || navigator.vendor;
  if (/android/i.test(userAgent)) return 'android';
  if (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1)
  ) {
    return 'ios';
  }
  return 'unknown';
};

export const getStoreUrl = (platform: Platform = getPlatform()): string =>
  platform === 'ios' ? STORE_URL_IOS : STORE_URL_ANDROID;

export const handleDeepLinkWithFallback = (
  deepLinkUrl: string,
  storeUrl: string,
  timeoutMs = 500
): void => {
  let hasSwitched = false;
  const markAsSwitched = (): void => {
    hasSwitched = true;
  };

  document.addEventListener('visibilitychange', markAsSwitched);
  window.addEventListener('blur', markAsSwitched);
  window.addEventListener('pagehide', markAsSwitched);

  window.location.href = deepLinkUrl;

  setTimeout(() => {
    document.removeEventListener('visibilitychange', markAsSwitched);
    window.removeEventListener('blur', markAsSwitched);
    window.removeEventListener('pagehide', markAsSwitched);

    const isHidden = document.hidden || document.visibilityState === 'hidden';
    const hasLostFocus = !document.hasFocus();

    if (!hasSwitched && !isHidden && !hasLostFocus) {
      window.location.href = storeUrl;
    }
  }, timeoutMs);
};

export const openCurationInApp = ({
  curationId,
  overrideDeepLink,
}: {
  curationId: string | number;
  overrideDeepLink?: string;
}): void => {
  const platform = getPlatform();
  const storeUrl = getStoreUrl(platform);
  const userAgent = navigator.userAgent;
  const isInAppBrowser = IN_APP_BROWSER_REGEX.test(userAgent);
  const isSafari = SAFARI_REGEX.test(userAgent);
  const customSchemeUrl = `slayd://curations/v2/${curationId}`;

  if (isInAppBrowser) {
    if (platform === 'android') {
      window.location.href = overrideDeepLink ?? storeUrl;
    } else {
      window.location.href = customSchemeUrl;
      setTimeout(() => {
        window.location.href = overrideDeepLink ?? storeUrl;
      }, 1500);
    }
    return;
  }

  if (overrideDeepLink) {
    if (platform === 'ios') {
      window.location.href = overrideDeepLink.replace(
        'https://',
        'x-safari-https://'
      );
    } else {
      window.location.href = overrideDeepLink;
    }
    return;
  }

  if (platform === 'ios') {
    if (isSafari) {
      window.location.href = customSchemeUrl;
      setTimeout(() => {
        if (!document.hidden) {
          window.location.href = storeUrl;
        }
      }, 2000);
    } else {
      handleDeepLinkWithFallback(customSchemeUrl, storeUrl);
    }
  } else if (platform === 'android') {
    handleDeepLinkWithFallback(customSchemeUrl, storeUrl);
  } else {
    window.open(storeUrl, '_blank');
  }
};

export const openProductInApp = ({
  productId,
  fallback,
}: {
  productId: number | string | undefined;
  fallback: () => void;
}): void => {
  let deepLinkUrl = `slayd://product/${productId}`;
  const senderId = sessionStorage.getItem('instagramSenderId');
  if (senderId) {
    deepLinkUrl += `?sender_id=${senderId}`;
  }
  const timeout = 500;
  let hasSwitched = false;
  const visibilityChangeListener = (): void => {
    if (document.visibilityState === 'hidden') {
      hasSwitched = true;
    }
  };

  document.addEventListener('visibilitychange', visibilityChangeListener);
  window.location.href = deepLinkUrl;
  setTimeout(() => {
    document.removeEventListener('visibilitychange', visibilityChangeListener);
    if (!hasSwitched) {
      fallback();
    }
  }, timeout);
};

export const openSimilarProductInApp = ({
  imageUrls,
  fallback,
}: {
  imageUrls: string[];
  fallback: () => void;
}): void => {
  let deepLinkUrl = `slayd://similar-product/`;
  const senderId = sessionStorage.getItem('instagramSenderId');

  // Add image URLs as query parameters
  const params = new URLSearchParams();
  imageUrls.forEach((url) => {
    params.append('image_urls', url);
  });

  if (senderId) {
    params.append('sender_id', senderId);
  }

  if (params.toString()) {
    deepLinkUrl += `?${params.toString()}`;
  }

  const timeout = 500;
  let hasSwitched = false;
  const visibilityChangeListener = (): void => {
    if (document.visibilityState === 'hidden') {
      hasSwitched = true;
    }
  };

  document.addEventListener('visibilitychange', visibilityChangeListener);
  window.location.href = deepLinkUrl;
  setTimeout(() => {
    document.removeEventListener('visibilitychange', visibilityChangeListener);
    if (!hasSwitched) {
      fallback();
    }
  }, timeout);
};
